import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { EMPLOYEE_POPOVER_PLACEMENT } from '@app/shared/sb-lib/employee-popover-placement.token';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipDirective } from '@sb/tooltip';
import { SbDialogService } from '@sb/ui/dialog';
import { EmployeePopoverComponent } from '@sb/ui/employee-popover';
import { PopoverDirective, PopoverModule } from '@sb/ui/popover';
import { SummaryItemComponent } from '@sb/ui/summary-item';

import { AbsenceOptionUnit } from '../../../enums';
import { DecimalToDurationFormatPipe } from '../../../pipes/decimal-to-duration-format.pipe';
import { EnhancedAbsenceModel } from '../../../reducers/orm/absence/absence.model';
import { AvatarBadgeGroupComponent } from '../../sb-lib/avatar-badge-group/avatar-badge-group.component';

export interface EmployeePopoverData {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  phone?: string;
  email?: string;
  contractEndDate?: string;
  showProfileButton?: boolean;
  location?: string;
  department?: string;
  team?: string;
  jobTitle?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'absence-summary-item',
  imports: [
    AvatarBadgeGroupComponent,
    TranslatePipe,
    LocaleDatePipe,
    DecimalToDurationFormatPipe,
    NgTemplateOutlet,
    TooltipDirective,
    SummaryItemComponent,
    NgClass,
    PopoverModule,
    EmployeePopoverComponent,
  ],
  templateUrl: './absence-summary-item.component.html',
  providers: [SbDialogService],
})
export class AbsenceSummaryItemComponent {
  private router = inject(Router);
  private dialogService = inject(SbDialogService);

  AbsenceOptionUnit = AbsenceOptionUnit;
  private _absence: EnhancedAbsenceModel;

  popoverPosition = inject(EMPLOYEE_POPOVER_PLACEMENT);

  @ViewChild(PopoverDirective)
  private popover: PopoverDirective;

  @ViewChild(AvatarBadgeGroupComponent)
  private avatarBadgeGroup: AvatarBadgeGroupComponent;

  @Input()
  set absence(absence: EnhancedAbsenceModel) {
    this._absence = absence;
  }
  get absence(): EnhancedAbsenceModel {
    return this._absence;
  }

  @Input()
  isOwnAbsence: boolean;

  @Input()
  showStatus: boolean;

  @Input()
  rangeClickShouldNavigate: boolean;

  // Display data related to date range and duration inline
  @Input()
  inline: boolean;

  // Use smaller font sizes (used when used as a notification item in the sidebar)
  @Input()
  compact: boolean;

  @Input()
  popoverData: EmployeePopoverData;

  redirectToSchedule(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.rangeClickShouldNavigate) {
      this.router.navigate(['schedule', 'employee', 'week', { date: this.absence.startdate }]);
    }
  }

  goToEmployeePage() {
    this.dialogService.closeAll();
    this.popover.close();
    this.avatarBadgeGroup.popover.close();
    this.router.navigate(['employees', this.absence.Employee.id]);
  }
}
